import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react'
import Container from '../../Components/Container'
import DataTable from 'react-data-table-component'
import toast, { Toaster } from 'react-hot-toast'
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiSearch, FiX } from 'react-icons/fi'
import { motion, AnimatePresence } from 'framer-motion'

const readJson = async (res) => {
    if (!res.ok) throw new Error('Sunucu yanıt vermedi')
    return res.json()
}

const isListError = (payload) =>
    payload != null &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    payload.status === 400

/** Sunucu 400 dışında yanıtı başarı sayıyor (mevcut users.php ile uyumlu) */
const isMutationSuccess = (data) =>
    data != null &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Number(data.status) !== 400

const roleLabel = (role) => {
    const r = String(role ?? '').toLowerCase()
    if (r === 'admin') return 'Yönetici'
    if (r === 'user') return 'Personel'
    return role ? String(role) : '—'
}

const Users = () => {
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [surname, setSurname] = useState('')
    const [phone, setPhone] = useState('')
    const [role, setRole] = useState('')
    const [password, setPassword] = useState('')
    const [isEditMode, setIsEditMode] = useState(false)
    const [editingId, setEditingId] = useState(null)
    const [rows, setRows] = useState([])
    const [filterText, setFilterText] = useState('')
    const [listLoading, setListLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const firstFieldRef = useRef(null)

    const loadUsers = useCallback(async () => {
        const formdata = new FormData()
        formdata.append('action', 'select')
        const payload = await fetch(
            process.env.REACT_APP_BASE_URL + 'users.php',
            { method: 'POST', body: formdata }
        ).then(readJson)

        if (isListError(payload)) {
            toast.error(payload.message || 'Liste alınamadı')
            setRows([])
            return
        }
        setRows(Array.isArray(payload) ? payload : [])
    }, [])

    useEffect(() => {
        let cancelled = false
        setListLoading(true)
        ;(async () => {
            try {
                await loadUsers()
            } catch {
                if (!cancelled) {
                    toast.error('Kullanıcılar yüklenirken hata oluştu')
                    setRows([])
                }
            } finally {
                if (!cancelled) setListLoading(false)
            }
        })()
        return () => {
            cancelled = true
        }
    }, [loadUsers])

    const resetForm = useCallback(() => {
        setName('')
        setSurname('')
        setPhone('')
        setRole('')
        setPassword('')
        setIsEditMode(false)
        setEditingId(null)
    }, [])

    const closeModal = useCallback(() => {
        if (submitting) return
        setOpen(false)
        resetForm()
    }, [submitting, resetForm])

    const openCreateModal = () => {
        resetForm()
        setOpen(true)
    }

    const openEditModal = (row) => {
        setIsEditMode(true)
        setEditingId(row.id)
        setName(row.name ?? '')
        setSurname(row.surname ?? '')
        setPhone(row.phone ?? '')
        setRole(row.role ?? '')
        setPassword('')
        setOpen(true)
    }

    useEffect(() => {
        if (!open) return
        const raf = requestAnimationFrame(() => firstFieldRef.current?.focus())
        const onKey = (e) => {
            if (e.key === 'Escape' && !submitting) closeModal()
        }
        document.addEventListener('keydown', onKey)
        const prev = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        return () => {
            cancelAnimationFrame(raf)
            document.removeEventListener('keydown', onKey)
            document.body.style.overflow = prev
        }
    }, [open, submitting, closeModal])

    const validateForm = (forInsert) => {
        if (!name.trim() || !surname.trim() || !phone.trim() || !role) {
            toast.error('Ad, soyad, telefon ve rol zorunludur')
            return false
        }
        if (forInsert && !password.trim()) {
            toast.error('Yeni kullanıcı için şifre girin')
            return false
        }
        return true
    }

    const submitInsert = async () => {
        if (!validateForm(true)) return
        setSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'insert')
            formdata.append('name', name.trim())
            formdata.append('surname', surname.trim())
            formdata.append('phone', phone.trim())
            formdata.append('role', role)
            formdata.append('password', password)
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'users.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isMutationSuccess(data)) {
                toast.error(data.message || 'Kayıt başarısız')
                return
            }
            toast.success(data.message || 'Kullanıcı eklendi')
            setOpen(false)
            resetForm()
            await loadUsers()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setSubmitting(false)
        }
    }

    const submitUpdate = async () => {
        if (!validateForm(false)) return
        if (editingId == null) {
            toast.error('Kayıt seçilemedi')
            return
        }
        setSubmitting(true)
        try {
            const formdata = new FormData()
            formdata.append('action', 'update')
            formdata.append('id', String(editingId))
            formdata.append('name', name.trim())
            formdata.append('surname', surname.trim())
            formdata.append('phone', phone.trim())
            formdata.append('role', role)
            if (password.trim()) {
                formdata.append('password', password)
            }
            const data = await fetch(
                process.env.REACT_APP_BASE_URL + 'users.php',
                { method: 'POST', body: formdata }
            ).then(readJson)

            if (!isMutationSuccess(data)) {
                toast.error(data.message || 'Güncelleme başarısız')
                return
            }
            toast.success(data.message || 'Kullanıcı güncellendi')
            setOpen(false)
            resetForm()
            await loadUsers()
        } catch {
            toast.error('İstek başarısız oldu')
        } finally {
            setSubmitting(false)
        }
    }

    const confirmDelete = async (rowId) => {
        const formdata = new FormData()
        formdata.append('action', 'delete')
        formdata.append('id', String(rowId))
        const data = await fetch(
            process.env.REACT_APP_BASE_URL + 'users.php',
            { method: 'POST', body: formdata }
        ).then(readJson)

        if (!isMutationSuccess(data)) {
            throw new Error(data.message || 'Silinemedi')
        }
        await loadUsers()
        return data
    }

    const deleteUser = (row) => {
        toast(
            (t) => (
                <div className='flex flex-col gap-4'>
                    <p className='text-sm leading-relaxed text-slate-700'>
                        <span className='font-semibold text-slate-900'>
                            {row.name} {row.surname}
                        </span>{' '}
                        kullanıcısını silmek istediğinize emin misiniz?
                    </p>
                    <div className='flex justify-end gap-2'>
                        <button
                            type='button'
                            className='rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
                            onClick={() => toast.dismiss(t.id)}
                        >
                            İptal
                        </button>
                        <button
                            type='button'
                            className='rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-red-700'
                            onClick={() => {
                                toast.dismiss(t.id)
                                toast.promise(confirmDelete(row.id), {
                                    loading: 'Siliniyor…',
                                    success: (d) =>
                                        String(
                                            d?.message || 'Kullanıcı silindi'
                                        ),
                                    error: (e) =>
                                        e.message || 'Silme başarısız',
                                })
                            }}
                        >
                            Sil
                        </button>
                    </div>
                </div>
            ),
            { duration: 8000 }
        )
    }

    const filteredRows = useMemo(() => {
        const q = filterText.trim().toLowerCase()
        if (!q) return rows
        return rows.filter((row) => {
            const blob = [
                row.name,
                row.surname,
                row.phone,
                row.role,
            ]
                .map((x) => String(x ?? '').toLowerCase())
                .join(' ')
            return blob.includes(q)
        })
    }, [rows, filterText])

    const columns = useMemo(
        () => [
            {
                name: 'No',
                selector: (r) => r.id,
                sortable: true,
                width: '80px',
            },
            {
                name: 'İsim',
                selector: (r) => r.name,
                sortable: true,
            },
            {
                name: 'Soyisim',
                selector: (r) => r.surname,
                sortable: true,
            },
            {
                name: 'Telefon',
                selector: (r) => r.phone,
                sortable: true,
            },
            {
                name: 'Rol',
                cell: (r) => {
                    const isAdmin = String(r.role ?? '').toLowerCase() === 'admin'
                    return (
                        <span
                            className={`rounded-full px-3 py-1 text-sm ${
                                isAdmin
                                    ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-slate-100 text-slate-700'
                            }`}
                        >
                            {roleLabel(r.role)}
                        </span>
                    )
                },
                sortable: true,
            },
            {
                name: 'İşlem',
                cell: (r) => (
                    <div className='flex items-center gap-2'>
                        <button
                            type='button'
                            onClick={() => openEditModal(r)}
                            className='rounded-xl p-2 text-indigo-600 transition hover:bg-indigo-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2'
                            title='Düzenle'
                        >
                            <FiEdit2 size={18} />
                        </button>
                        <button
                            type='button'
                            onClick={() => deleteUser(r)}
                            className='rounded-xl p-2 text-red-600 transition hover:bg-red-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2'
                            title='Sil'
                        >
                            <FiTrash2 size={18} />
                        </button>
                    </div>
                ),
                width: '120px',
            },
        ],
        []
    )

    const customStyles = useMemo(
        () => ({
            table: {
                style: {
                    backgroundColor: 'white',
                    borderRadius: '0.75rem',
                },
            },
            headRow: {
                style: {
                    backgroundColor: '#f8fafc',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e2e8f0',
                },
            },
            headCells: {
                style: {
                    color: '#64748b',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '1rem',
                },
            },
            cells: {
                style: {
                    fontSize: '0.875rem',
                    padding: '1rem',
                    color: '#334155',
                },
            },
        }),
        []
    )

    const inputClass =
        'w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60'

    return (
        <Container padding bg='#f8fafc'>
            <Toaster
                position='top-right'
                toastOptions={{ className: 'text-sm font-medium shadow-lg' }}
            />

            <AnimatePresence>
                {open && (
                    <motion.div
                        key='user-modal'
                        role='presentation'
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className='fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center'
                        onClick={() => !submitting && closeModal()}
                    >
                        <motion.div
                            role='dialog'
                            aria-modal='true'
                            initial={{ scale: 0.96, opacity: 0, y: 12 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.96, opacity: 0, y: 12 }}
                            transition={{
                                type: 'spring',
                                damping: 26,
                                stiffness: 320,
                            }}
                            className='w-full max-w-md overflow-hidden rounded-t-2xl border border-slate-200 bg-white shadow-2xl sm:rounded-2xl'
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className='border-b border-slate-100 px-6 py-5'>
                                <div className='flex items-start justify-between gap-3'>
                                    <h2 className='text-lg font-bold text-slate-900'>
                                        {isEditMode
                                            ? 'Kullanıcı düzenle'
                                            : 'Yeni kullanıcı'}
                                    </h2>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            !submitting && closeModal()
                                        }
                                        disabled={submitting}
                                        className='rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-50'
                                        aria-label='Kapat'
                                    >
                                        <FiX size={20} />
                                    </button>
                                </div>
                            </div>
                            <div className='space-y-4 px-6 py-6'>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Ad
                                    </label>
                                    <input
                                        ref={firstFieldRef}
                                        type='text'
                                        value={name}
                                        onChange={(e) =>
                                            setName(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Ad'
                                        autoComplete='given-name'
                                    />
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Soyad
                                    </label>
                                    <input
                                        type='text'
                                        value={surname}
                                        onChange={(e) =>
                                            setSurname(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Soyad'
                                        autoComplete='family-name'
                                    />
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Telefon
                                    </label>
                                    <input
                                        type='tel'
                                        value={phone}
                                        onChange={(e) =>
                                            setPhone(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder='Telefon'
                                        autoComplete='tel'
                                    />
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Şifre
                                    </label>
                                    <input
                                        type='password'
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                        placeholder={
                                            isEditMode
                                                ? 'Değiştirmek için yeni şifre'
                                                : 'Şifre'
                                        }
                                        autoComplete={
                                            isEditMode
                                                ? 'new-password'
                                                : 'new-password'
                                        }
                                    />
                                </div>
                                <div>
                                    <label className='mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-500'>
                                        Rol
                                    </label>
                                    <select
                                        value={role}
                                        onChange={(e) =>
                                            setRole(e.target.value)
                                        }
                                        disabled={submitting}
                                        className={inputClass}
                                    >
                                        <option value=''>Rol seçin</option>
                                        <option value='admin'>Yönetici</option>
                                        <option value='user'>Personel</option>
                                    </select>
                                </div>
                            </div>
                            <div className='flex justify-end gap-3 border-t border-slate-100 bg-slate-50/80 px-6 py-4'>
                                <button
                                    type='button'
                                    onClick={closeModal}
                                    disabled={submitting}
                                    className='rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50'
                                >
                                    İptal
                                </button>
                                <button
                                    type='button'
                                    onClick={
                                        isEditMode
                                            ? submitUpdate
                                            : submitInsert
                                    }
                                    disabled={submitting}
                                    className='inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 transition hover:bg-indigo-700 disabled:pointer-events-none disabled:opacity-60'
                                >
                                    {submitting ? (
                                        <span className='h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent' />
                                    ) : isEditMode ? (
                                        'Güncelle'
                                    ) : (
                                        'Kaydet'
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className='mx-auto w-full max-w-[1400px] space-y-6 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
                <div className='rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm sm:p-8'>
                    <div className='flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between'>
                        <div className='flex items-start gap-4'>
                            <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-md shadow-indigo-500/25'>
                                <FiUsers className='text-xl' />
                            </div>
                            <div>
                                <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
                                    Kullanıcı yönetimi
                                </h1>
                                <p className='mt-1 text-sm text-slate-600'>
                                    Sistem kullanıcılarını yönetin.
                                </p>
                                <p className='mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700'>
                                    {filteredRows.length} kayıt
                                    {filterText.trim() ? ' (filtreli)' : ''}
                                </p>
                            </div>
                        </div>
                        <div className='flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center'>
                            <div className='relative min-w-0 sm:min-w-[16rem]'>
                                <FiSearch className='pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400' />
                                <input
                                    type='text'
                                    value={filterText}
                                    onChange={(e) =>
                                        setFilterText(e.target.value)
                                    }
                                    placeholder='Kullanıcı ara…'
                                    className={`${inputClass} pl-10`}
                                />
                            </div>
                            <button
                                type='button'
                                onClick={openCreateModal}
                                className='inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-indigo-700'
                            >
                                <FiPlus size={18} />
                                Yeni kullanıcı
                            </button>
                        </div>
                    </div>
                </div>

                <div className='overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm'>
                    <DataTable
                        columns={columns}
                        data={filteredRows}
                        pagination
                        progressPending={listLoading}
                        progressComponent={
                            <div className='flex flex-col items-center justify-center gap-3 py-20'>
                                <div className='h-10 w-10 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent' />
                                <p className='text-sm font-medium text-slate-600'>
                                    Yükleniyor…
                                </p>
                            </div>
                        }
                        customStyles={customStyles}
                        noDataComponent={
                            <div className='py-12 text-center text-sm text-slate-500'>
                                {filterText.trim()
                                    ? 'Aramaya uygun kullanıcı yok.'
                                    : 'Henüz kullanıcı yok.'}
                            </div>
                        }
                    />
                </div>
            </div>
        </Container>
    )
}

export default Users
